import React, { memo, useEffect, useMemo, useRef } from 'react';

import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import _findLastIndex from 'lodash/findLastIndex';
import _isEqual from 'lodash/isEqual';

import { analyticsService } from '../../../../../core/services';
import { RemoveBasketItemType } from '../../../../../types/basket';
import {
  MenuActionsType,
  MenuSectionDataType,
} from '../../../../../types/menu';
import { menuUtils } from '../../../../../utils';
import { Track } from '../../../../../weborder/analytics';
import * as Api from '../../../../../weborder/Api';
import { Data } from '../../../../../weborder/BasketControl';
import { COLLAPSE_TIME } from '../../../../../weborder/constants/Constants';
import { ExpandSection, MenuCard } from '../../../../molecules';

type MenuSectionProps = {
  actions: MenuActionsType;
  basket: Data;
  currency: Api.Currency;
  languageCode: string;
  isExpanded: boolean;
  menuId: number;
  menuSectionData: MenuSectionDataType;
  navBarHeight?: number;
  translate: any;
  vrId: number;
  waypoint?: any;
};

const viewportTopOffset = menuUtils.getViewportTopOffset();

const useStyles = makeStyles((theme: Theme) => ({
  sectionWrapper: {
    paddingTop: theme.spacing(2),
  },

  menuSection: {
    backgroundColor: theme.palette.common.white,
    margin: theme.spacing(4, 0, 0),
  },

  sectionTitle: {
    paddingTop: theme.spacing(2),
    scrollMarginTop: `${viewportTopOffset}px`,
    scrollSnapMarginTop: `${viewportTopOffset}px`,

    '& [class*="MuiAccordionSummary-root MuiExpanded"]': {
      paddingTop: theme.spacing(4),
    },

    '&:before': {
      display: 'none',
    },
  },

  menuItems: {
    width: '100%',

    '&:first-child': {
      borderTop: `1px solid ${theme.palette.grey[400]}`,
    },
  },

  menuItem: {
    boxSizing: 'border-box',
    width: '100%',
  },

  [theme.breakpoints.up('sm')]: {
    sectionWrapper: {
      paddingTop: theme.spacing(3),
    },
    sectionTitle: {
      padding: theme.spacing(3, 3, 0),

      '& .MuiAccordionSummary-content, & .MuiAccordionSummary-expandIcon': {
        alignItems: 'center',
        display: 'flex',
      },
    },

    menuItems: {
      columnCount: 2,
      columnGap: theme.spacing(2),
      columnWidth: 'auto',
      marginTop: theme.spacing(-1),
      paddingBottom: theme.spacing(2),

      '&:first-child': {
        borderTop: 'none',
      },
    },

    menuItem: {
      breakInside: 'avoid',
      marginBottom: theme.spacing(2),
      position: 'relative',
    },

    '@media not all and (min-resolution:.001dpcm) { @supports (-webkit-appearance:none) and (display:flow-root)':
      {
        menuItem: {
          display: 'inline-block',
          marginBottom: theme.spacing(1),
          marginTop: theme.spacing(0.5),
          width: '100%',
        },
      },
  },

  [theme.breakpoints.up('md')]: {
    sectionTitle: {
      padding: 0,

      '& .MuiAccordionSummary-content, & .MuiAccordionSummary-expandIcon': {
        margin: theme.spacing(4, 0, 0),

        '&.Mui-expanded': {
          marginBottom: theme.spacing(2.5),
        },
      },
    },

    menuItems: {
      padding: 0,
      paddingBottom: theme.spacing(2),
    },
  },
}));

function MenuSectionComponent(props: MenuSectionProps): JSX.Element {
  const {
    actions,
    basket,
    currency,
    languageCode,
    isExpanded,
    menuId,
    menuSectionData,
    navBarHeight = 0,
    translate,
    vrId,
    waypoint,
  } = props;

  const { description, expandBehaviour, imageUrl, items, name, sectionId } =
    menuSectionData;
  const classes = useStyles();

  const theme = useTheme();
  const isTwoColumnMenuLayout = useMediaQuery(theme.breakpoints.up('sm'));
  const hasCompactMenu = useMediaQuery(theme?.breakpoints.down('sm'));

  const performClickMenuRef = useRef(false);

  useEffect(() => {
    if (isExpanded && performClickMenuRef.current) {
      const elementId = `menuSection-${sectionId.toString()}`;
      performClickMenuRef.current = false;

      setTimeout(() => {
        menuUtils.handleScrollToSection(
          elementId,
          hasCompactMenu,
          navBarHeight,
        );
      }, COLLAPSE_TIME);
    }
  }, [isExpanded]);

  const getAnalyticsData = (
    item: Api.MenuItem,
    itemType: string,
  ): Record<string, boolean | number | string> => ({
    amp_itemAddedFrom: 'Menu Area',
    amp_itemCount: 1,
    amp_itemName: item.Name,
    amp_itemPrice: item.Price,
    amp_label: 'Item Added to Basket',
    amp_productId: item.MenuItemId.toString(),
    eventLabel: item.Name,
    itemType,
    label: 'Add Item To Basket Via Options',
    menuId,
    name: item.Name,
    section: 'Restaurants',
    virtualRestaurantId: vrId,
  });

  const toggleExpandedState = (): void => {
    if (!isExpanded) {
      performClickMenuRef.current = true;

      if (expandBehaviour === Api.MenuSectionBehaviour.ExpandSingle) {
        actions.initExpanded(vrId, [sectionId]);
      } else {
        actions.onAddExpanded(vrId, sectionId);
      }
    } else {
      actions.onRemoveExpanded(vrId, sectionId);
    }
  };

  const reorderItemsForTwoColumnLayout = (menuItems) => {
    const column1 = [];
    const column2 = [];
    menuItems.forEach((item, index) =>
      index % 2 === 0 ? column1.push(item) : column2.push(item),
    );
    return [...column1, ...column2];
  };

  const handleIncrementItem = (event, item): null => {
    const lastItemOptionAdded = basket?.items
      .filter((basketItem) => basketItem?.Item?.MenuItemId === item.MenuItemId)
      .slice(-1)[0];
    if (!lastItemOptionAdded) {
      event.preventDefault();
      return null;
    }

    Track(
      'Restaurants',
      'Add Item To Basket Via Options',
      getAnalyticsData(item, 'complex'),
    );
    const addItemData = {
      Item: lastItemOptionAdded.Item,
      Options: lastItemOptionAdded.Options,
    };
    menuUtils.handleAddItem(actions.onAddItem, addItemData);
    event.stopPropagation();
    return null;
  };

  const renderMenuCard = (
    item: Api.MenuItem,
    basketCount: number,
    basketIndex: number,
  ): JSX.Element => {
    const hasMenuOptions = !!item.MenuItemOptionSets.length;

    const onHandleItemClick = () => actions.onItemClick({ menuId, item });

    const onHandleAddItem = (event) => {
      if (item.MenuItemOptionSets.length > 0) {
        handleIncrementItem(event, item);
      } else {
        event.stopPropagation();

        Track(
          'Restaurants',
          'Add Item To Basket Via Options',
          getAnalyticsData(item, 'simple'),
        );

        actions.onAddItem({
          Count: 1,
          Item: item,
          Options: [],
        });
      }
    };

    const onHandleRemoveItem = (event) => {
      event.stopPropagation();

      const lastIndexFound = _findLastIndex(
        basket.items,
        (basketItem) => basketItem.Item.MenuItemId === item.MenuItemId,
      );
      const removeItemData = {
        index: lastIndexFound !== -1 ? lastIndexFound : basketIndex,
        item,
        menuId,
      } as RemoveBasketItemType;

      const eventData = {
        amp_itemName: item.Name,
        amp_itemPrice: item.Price,
        amp_itemRemovedFrom: 'Menu Area',
        amp_label: 'Item Removed from Basket',
      };
      analyticsService.track(eventData);

      return menuUtils.handleRemoveItem(actions.onRemoveItem, removeItemData);
    };

    const menuCardActions = {
      onAddItem: onHandleAddItem,
      onClickItem: onHandleItemClick,
      onRemoveItem: onHandleRemoveItem,
    };

    return (
      <MenuCard
        actions={menuCardActions}
        basketCount={basketCount}
        className={classes.menuItem}
        currency={currency}
        hasMenuOptions={hasMenuOptions}
        key={item.MenuItemId}
        languageCode={languageCode}
        menuItem={item}
        translate={translate}
        vrId={vrId}
      />
    );
  };

  const getMenuCardDeck = (menuItems) =>
    menuItems.map((item) => {
      const basketCount = basket?.items.filter(
        (basketItem) => basketItem.Item.MenuItemId === item.MenuItemId,
      ).length;

      const basketIndex = basket?.items.findIndex(
        (basketItem) => basketItem.Item.MenuItemId === item.MenuItemId,
      );

      return renderMenuCard(item, basketCount, basketIndex);
    });

  const itemsOrderedForLayout = useMemo(() => {
    const displayOrderedMenuItems = [...items].sort(
      (a, b) => a.DisplayOrder - b.DisplayOrder,
    );

    const itemsForTwoColumnLayout = reorderItemsForTwoColumnLayout(
      displayOrderedMenuItems,
    );

    return isTwoColumnMenuLayout
      ? itemsForTwoColumnLayout
      : displayOrderedMenuItems;
  }, [isTwoColumnMenuLayout]);

  const menuItemsDeck = getMenuCardDeck(itemsOrderedForLayout);

  return (
    <div className={classes.sectionWrapper}>
      {waypoint}

      <ExpandSection
        className={classes.sectionTitle}
        expanded={isExpanded}
        id={`menuSection-${sectionId.toString()}`}
        imageUrl={imageUrl}
        onChange={toggleExpandedState}
        sectionDescription={description}
        sectionName={name}
        TransitionProps={{
          mountOnEnter: true,
          timeout: 0,
          unmountOnExit: true,
        }}
        translate={translate}
      >
        <div className={classes.menuItems} role="list">
          {menuItemsDeck}
        </div>
      </ExpandSection>
    </div>
  );
}

function isMenuSectionPropsEqual(prevMenuSection, nextMenuSection): boolean {
  const menuSectionBasketItems = (items, sectionId) => {
    if (!items) {
      return [];
    }

    return items.reduce((acc, i) => {
      if (i.Item.MenuSectionId === sectionId) {
        acc.push(i.Item.MenuItemId);
      }
      return acc;
    }, []);
  };

  let prevBasketItems = [];
  let nextBasketItems = [];
  const isBasketEqual = _isEqual(
    prevMenuSection.basket?.items,
    nextMenuSection.basket?.items,
  );

  if (nextMenuSection.isExpanded && !isBasketEqual) {
    prevBasketItems = menuSectionBasketItems(
      prevMenuSection.basket.items,
      prevMenuSection.menuSectionData.sectionId,
    );
    nextBasketItems = menuSectionBasketItems(
      nextMenuSection.basket.items,
      nextMenuSection.menuSectionData.sectionId,
    );
  }

  return (
    prevMenuSection.sectionId === nextMenuSection.sectionId &&
    prevMenuSection.isExpanded === nextMenuSection.isExpanded &&
    prevBasketItems.length === nextBasketItems.length
  );
}

const MenuSection = memo(MenuSectionComponent, isMenuSectionPropsEqual);
MenuSection.displayName = 'MenuSection';

export { MenuSection };
